// Pure gesture maths shared by the pointer handler and its tests. All
// positions are canvas-local CSS pixels, the space `CameraState.tx/ty` live in.
import type { CameraState } from '../types.ts';
import { zoomAt } from '../render/camera.ts';

export interface Pt { x: number; y: number }

/** Centroid and finger distance of a two-pointer gesture. */
export interface Pinch { cx: number; cy: number; d: number }

export function pinchOf(a: Pt, b: Pt): Pinch {
  return { cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2, d: Math.hypot(b.x - a.x, b.y - a.y) };
}

/** Fingers closer than this (px) give no usable scale; the gesture only pans. */
export const PINCH_MIN_DIST = 8;

/**
 * Advance the camera from one two-finger sample to the next: scale about the
 * previous centroid by the change in finger distance, then translate by the
 * centroid's motion. For a pure scale+translate finger motion the world points
 * under both fingers stay under them.
 */
export function applyPinch(cam: CameraState, prev: Pinch, next: Pinch): void {
  if (prev.d > PINCH_MIN_DIST && next.d > PINCH_MIN_DIST) {
    const f = next.d / prev.d;
    if (isFinite(f) && f > 0) zoomAt(cam, prev.cx, prev.cy, f);
  }
  cam.tx += next.cx - prev.cx;
  cam.ty += next.cy - prev.cy;
}

/** One mouse-wheel notch (100 px) zooms by this factor; the legacy viewer used 1.1. */
export const WHEEL_NOTCH_FACTOR = 1.1;
const WHEEL_MAX_PX = 200;

/**
 * Zoom factor for a wheel event. Continuous in the delta, so trackpad pinches
 * (many small deltas) are smooth, and symmetric: +d then -d is a no-op.
 * deltaMode 1 = lines, 2 = pages (Firefox); both are mapped to pixels.
 */
export function wheelZoomFactor(deltaY: number, deltaMode = 0): number {
  if (!deltaY || !isFinite(deltaY)) return 1;
  let px = deltaMode === 1 ? deltaY * 40 : deltaMode === 2 ? deltaY * 800 : deltaY;
  px = Math.max(-WHEEL_MAX_PX, Math.min(WHEEL_MAX_PX, px));
  return Math.exp(-px * Math.log(WHEEL_NOTCH_FACTOR) / 100);
}

export const DOUBLE_TAP_MS = 320;
export const TAP_SLOP_PX = 12;      // finger may drift this far and still be a tap
export const DOUBLE_TAP_DIST_PX = 40; // the two taps must land this close together
export const DOUBLE_TAP_ZOOM = 2;

/** Detects a double tap from (down, up) pairs of a single touch pointer. */
export class DoubleTap {
  private lastTap: { x: number; y: number; t: number } | null = null;

  /** Report a pointer-up. Returns true when it completes a double tap. */
  up(down: Pt, downT: number, upPos: Pt, upT: number): boolean {
    const moved = Math.hypot(upPos.x - down.x, upPos.y - down.y) > TAP_SLOP_PX;
    const slow = upT - downT > DOUBLE_TAP_MS;
    if (moved || slow) { this.lastTap = null; return false; }
    const prev = this.lastTap;
    this.lastTap = { x: upPos.x, y: upPos.y, t: upT };
    if (!prev) return false;
    const isDouble = upT - prev.t <= DOUBLE_TAP_MS && Math.hypot(upPos.x - prev.x, upPos.y - prev.y) <= DOUBLE_TAP_DIST_PX;
    if (isDouble) this.lastTap = null;   // a triple tap starts over
    return isDouble;
  }

  reset(): void { this.lastTap = null; }
}
